import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  CapGainFilterType,
  CapGainListing,
  CapGainListingRecordsCount,
  CapGainListingTotalPages,
  CapGainsState,
} from '@products/types/tax.type';
import { ProductDetailConfiguration } from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { UsCapitalGainsService } from '../services/us-capital-gains.service';
import { TaxDisplayComponent } from '../tax-component.interface';
import { TaxConfig } from '../tax.config';
import cloneDeep from 'lodash/cloneDeep';

const logger = Logger.getLogger('USCapitalGainsComponent');

@Component({
  selector: 'ft-us-capital-gains',
  templateUrl: './us-capital-gains.component.html',
  styleUrls: ['./us-capital-gains.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsCapitalGainsComponent
  extends BaseProductDetailComponent
  implements TaxDisplayComponent, OnDestroy {
  componentConfiguration: TaxConfig;
  dataSubscription: Subscription[] = [];
  gridListing: CapGainListing[] = [];
  gridListingTotalPages: CapGainListingTotalPages;
  recordsCount: CapGainListingRecordsCount;
  filters: CapGainFilterType[];
  isFilterProcessing: boolean;
  hasNoShareClass: boolean;
  private currentPageNumber = 1;
  public isPopulated = false;

  constructor(
    private capitalGainsService: UsCapitalGainsService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  populate(
    componentConfig: TaxConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);

    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();
    if (this.brConfig) {
      this.dataSubscription.push(
        this.capitalGainsService.capGainsState.subscribe(
          (state: CapGainsState) => this.mapState(state)
        )
      );
      this.dataSubscription.push(
        this.capitalGainsService.getCurrentPage$().subscribe((gridListing) => {
          this.gridListing = cloneDeep(gridListing);
          this.recordsCount = cloneDeep({
            // intentionally done for total & filtered, as listing component needs this object to have change event when size changes.
            total: this.recordsCount?.total,
            filtered: this.recordsCount?.filtered,
            size: this.capitalGainsService.getPageSize(),
          });
        })
      );
      this.dataSubscription.push(
        this.capitalGainsService.getNumberOfPages$().subscribe((totalPages) => {
          this.gridListingTotalPages = cloneDeep({
            count: totalPages,
          });
        })
      );
      this.dataSubscription.push(
        this.capitalGainsService.getPageNumber$().subscribe((pageNumber) => {
          // *** Scroll Behaviour Conditions ***
          // 1. At page load, current page number would be same as the returned one. So, it block from being scrolled.
          //    This, condition is required as Pagination Component by default emits pageChange event (1st page number) on load.
          // 2. When filter is processed, then definetly return 'true' value.
          if (
            this.currentPageNumber !== pageNumber ||
            this.capitalGainsService.isFilterProcessing
          ) {
            this.isFilterProcessing = cloneDeep(
              this.capitalGainsService.isFilterProcessing
            );
          }
          this.currentPageNumber = pageNumber;
        })
      );

      this.capitalGainsService.populate();
    }
  }

  /**
   * Called when state changes.
   * @param state the state returned by capital gains service
   */
  mapState(state: CapGainsState): void {
    this.filters = state.filters;

    this.hasNoShareClass = state.hasNoShareClass;

    this.recordsCount = cloneDeep({
      total: state.capGainListing.length,
      filtered: state.gridListing.length,
      size: this.capitalGainsService.getPageSize(),
    });

    this.isPopulated = true;
    this.changeDetector.detectChanges();
  }

  /**
   * Trigger page change with the page number selected.
   * @param pageNumber page number selected
   */
  onPageChanged(pageNumber: number): void {
    this.capitalGainsService.goToPage(pageNumber);
  }

  /**
   * Trigger page size change with the size selected.
   * @param size page size selected
   */
  onDropdownChange(size: number): void {
    this.capitalGainsService.isFilterProcessing = false;
    this.capitalGainsService.setPageSize(size);
  }

  /**
   * Trigger grid filter as per the filter type & option selected.
   * @param searchValue search text if entered
   */
  filterListing(searchValue?: string): void {
    this.capitalGainsService.filterListing(searchValue);
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
